'use client';

import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/lib/auth-context';
import { AVAILABLE_LOCALES, useI18n } from '@/lib/i18n';
import SearchSuggestions, { saveRecent } from '@/components/layout/SearchSuggestions';

export type TopbarHamburgerRef = React.RefObject<HTMLButtonElement | null>;

const PAGE_TITLE_KEYS: Record<string, string> = {
  '/': 'nav.home',
  '/trending': 'nav.trending',
  '/tags': 'nav.all_tags',
  '/create': 'nav.new_prompt',
  '/my/prompts': 'nav.my_prompts',
  '/my/saved': 'nav.saved',
  '/following': 'nav.following',
  '/auth/login': 'nav.sign_in',
  '/admin': 'nav.admin',
};

function titleKeyFor(pathname: string): string {
  if (PAGE_TITLE_KEYS[pathname]) return PAGE_TITLE_KEYS[pathname];
  if (pathname.startsWith('/prompt/')) return 'nav.home';
  if (pathname.startsWith('/edit/')) return 'common.edit';
  if (pathname.startsWith('/profile/')) return 'nav.profile';
  return 'nav.home';
}

type Accent = 'violet' | 'sage' | 'clay' | 'ink';
const ACCENTS: { value: Accent; label: string; swatch: string }[] = [
  { value: 'violet', label: 'Violet', swatch: 'oklch(0.52 0.18 285)' },
  { value: 'sage',   label: 'Sage',   swatch: 'oklch(0.52 0.08 155)' },
  { value: 'clay',   label: 'Clay',   swatch: 'oklch(0.58 0.14 40)' },
  { value: 'ink',    label: 'Ink',    swatch: 'oklch(0.22 0.02 260)' },
];

export default function Topbar({ hamburgerRef }: { hamburgerRef?: TopbarHamburgerRef }) {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user, logout } = useAuth();
  const { locale, setLocale, t } = useI18n();
  // Seed from the URL so a deep-link like `/?search=GPT-4o` shows the term
  // in the input (otherwise live search would clobber the URL with empty).
  const [searchValue, setSearchValue] = useState(() => searchParams.get('search') ?? '');
  const [accent, setAccent] = useState<Accent>('violet');
  const [accentOpen, setAccentOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const accentRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const savedAccent = (localStorage.getItem('accent') as Accent) || 'violet';
    setAccent(savedAccent);
    function onClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (accentRef.current && !accentRef.current.contains(e.target as Node)) {
        setAccentOpen(false);
      }
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    // Sync aria-expanded when drawer opens/closes via event
    const onOpen = () => setDrawerOpen(true);
    const onClose = () => setDrawerOpen(false);
    window.addEventListener('mobiledrawer:open', onOpen);
    window.addEventListener('mobiledrawer:close', onClose);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      window.removeEventListener('mobiledrawer:open', onOpen);
      window.removeEventListener('mobiledrawer:close', onClose);
    };
  }, []);

  const changeAccent = (a: Accent) => {
    setAccent(a);
    const d = document.documentElement;
    if (a === 'violet') d.removeAttribute('data-accent');
    else d.setAttribute('data-accent', a);
    try { localStorage.setItem('accent', a); } catch { /* ignore */ }
    setAccentOpen(false);
  };

  const currentAccentSwatch = ACCENTS.find((a) => a.value === accent)?.swatch ?? ACCENTS[0].swatch;

  // ── Search w/ suggestion dropdown ────────────────────────────────
  //
  // Two pieces of state intentionally separated:
  //   - `searchValue` (immediate, drives the input + suggestion popover)
  //   - the URL `?search=` (committed only on Enter / suggestion-pick / idle)
  //
  // Typing into the input no longer hits the URL or the API. The popover
  // (rendered by <SearchSuggestions/>) filters local sources — recent
  // searches, AI models, tags. The actual gallery filter only fires when
  // the user "finishes": presses Enter, picks a suggestion, or stays idle
  // for ~700ms. This stops the previous behavior where every keystroke
  // hammered the API.
  const searchInputRef = useRef<HTMLInputElement>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  // Track the last-committed search to avoid pushing identical URLs.
  const lastCommittedRef = useRef<string>(searchParams.get('search') ?? '');

  const commitSearch = useCallback((value: string, directHref?: string) => {
    const trimmed = value.trim();
    saveRecent(trimmed);
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (directHref) {
      // Tag / library suggestions know their canonical URL.
      router.push(directHref);
      lastCommittedRef.current = trimmed;
      return;
    }
    if (trimmed === lastCommittedRef.current) return;
    lastCommittedRef.current = trimmed;
    if (pathname === '/' || pathname.startsWith('/?')) {
      router.replace(trimmed ? `/?search=${encodeURIComponent(trimmed)}` : '/');
    } else {
      router.push(trimmed ? `/?search=${encodeURIComponent(trimmed)}` : '/');
    }
  }, [pathname, router]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    if (!searchOpen) setSearchOpen(true);
    // "Stop typing" detector — fires a single commit ~700ms after the LAST
    // keystroke. Pressing Enter / picking a suggestion bypasses this.
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      commitSearch(value);
    }, 700);
  };

  // Submit (Enter) flushes any pending idle commit immediately.
  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    setSearchOpen(false);
    commitSearch(searchValue);
  };

  const handleLogout = () => {
    logout();
    setDropdownOpen(false);
    router.push('/');
  };

  const currentLocale = AVAILABLE_LOCALES.find((l) => l.code === locale) ?? AVAILABLE_LOCALES[0];

  return (
    <div className="topbar">
      {/* Hamburger — mobile only, opens the slide-in drawer */}
      <button
        ref={hamburgerRef}
        type="button"
        className="ps-btn ps-btn-icon ps-btn-ghost ps-mobile-menu-btn"
        aria-label="Open menu"
        aria-expanded={drawerOpen}
        onClick={() => {
          setDrawerOpen(true);
          window.dispatchEvent(new CustomEvent('mobiledrawer:open'));
        }}
      >
        <span className="material-symbols-outlined ps-icon">menu</span>
      </button>

      <Link href="/" className="topbar-mobile-brand no-underline" aria-label="Prompt Save home">
        <span className="mark">P</span>
        <span>Prompt Save</span>
      </Link>

      <div className="topbar-title">{t(titleKeyFor(pathname))}</div>

      <form className="search" onSubmit={handleSearch} role="search">
        <span className="material-symbols-outlined search-icon">search</span>
        <input
          ref={searchInputRef}
          id="global-search"
          type="text"
          placeholder={t('topbar.search_placeholder')}
          value={searchValue}
          onChange={(e) => handleSearchChange(e.target.value)}
          onFocus={() => setSearchOpen(true)}
          onClick={() => setSearchOpen(true)}
          onKeyDown={(e) => {
            // Forward arrow / Esc to the suggestion popover so users can
            // navigate the list with the keyboard. The popover handles it
            // via `onKeyDown` on its container — but the input has focus,
            // not the popover, so we mirror those keys here.
            if (e.key === 'Escape') {
              setSearchOpen(false);
            } else if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !searchOpen) {
              setSearchOpen(true);
            }
          }}
          autoComplete="off"
        />
        {/* Clicking the hint opens the command palette (same as ⌘K). */}
        <kbd
          style={{ cursor: 'pointer' }}
          onClick={(e) => {
            e.preventDefault();
            window.dispatchEvent(new CustomEvent('cmdpalette:open'));
          }}
          title="Open command palette"
        >
          ⌘K
        </kbd>
      </form>
      <SearchSuggestions
        inputRef={searchInputRef}
        query={searchValue}
        setQuery={setSearchValue}
        onCommit={(q, href) => commitSearch(q, href)}
        open={searchOpen}
        setOpen={setSearchOpen}
      />

      {/* Language switcher — first of the trailing right-side items.
          `margin-left: auto` here pushes this AND every sibling that follows
          (accent / theme / divider / sign in / sign up) all the way to the
          right edge, regardless of how wide the search input is. */}
      <div ref={langRef} className="relative topbar-lang" style={{ marginLeft: 'auto' }}>
        <button
          type="button"
          onClick={() => setLangOpen((o) => !o)}
          className="ps-btn ps-btn-icon ps-btn-ghost"
          aria-label={t('topbar.language')}
          title={t('topbar.language')}
          style={{ fontSize: 14 }}
        >
          <span style={{ fontSize: 16 }}>{currentLocale.flag}</span>
        </button>
        {langOpen && (
          <div
            className="absolute right-0 mt-2 z-50"
            style={{
              background: 'var(--bg-raised)',
              border: '1px solid var(--line)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-lg)',
              padding: 8,
              minWidth: 150,
            }}
          >
            <div style={{
              fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.09em',
              color: 'var(--ink-3)', padding: '4px 10px 8px', fontWeight: 500,
            }}>
              {t('topbar.language')}
            </div>
            {AVAILABLE_LOCALES.map((l) => (
              <button
                key={l.code}
                onClick={() => { setLocale(l.code); setLangOpen(false); }}
                className="flex items-center gap-2.5 w-full px-2.5 py-1.5 rounded-md"
                style={{
                  background: locale === l.code ? 'var(--bg-soft)' : 'transparent',
                  color: 'var(--ink)',
                  fontSize: 13,
                  fontFamily: 'var(--font-sans)',
                }}
              >
                <span aria-hidden style={{ fontSize: 16 }}>{l.flag}</span>
                <span style={{ flex: 1, textAlign: 'left' }}>{l.label}</span>
                {locale === l.code && (
                  <span className="material-symbols-outlined" style={{ fontSize: 14, color: 'var(--accent)' }}>check</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Accent swap */}
      <div ref={accentRef} className="relative">
        <button
          type="button"
          onClick={() => setAccentOpen((o) => !o)}
          className="ps-btn ps-btn-icon ps-btn-ghost"
          aria-label="Change accent color"
          title="Accent color"
          style={{ position: 'relative' }}
        >
          <span
            aria-hidden
            style={{
              width: 14, height: 14, borderRadius: '50%',
              background: currentAccentSwatch,
              boxShadow: '0 0 0 1px var(--line) inset',
              display: 'inline-block',
            }}
          />
        </button>
        {accentOpen && (
          <div
            className="absolute right-0 mt-2 z-50"
            style={{
              background: 'var(--bg-raised)',
              border: '1px solid var(--line)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-lg)',
              padding: 8,
              minWidth: 160,
            }}
          >
            <div style={{
              fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.09em',
              color: 'var(--ink-3)', padding: '4px 10px 8px', fontWeight: 500,
            }}>
              Accent
            </div>
            {ACCENTS.map((a) => (
              <button
                key={a.value}
                onClick={() => changeAccent(a.value)}
                className="flex items-center gap-2.5 w-full px-2.5 py-1.5 rounded-md"
                style={{
                  background: accent === a.value ? 'var(--bg-soft)' : 'transparent',
                  color: 'var(--ink)',
                  fontSize: 13,
                  fontFamily: 'var(--font-sans)',
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 14, height: 14, borderRadius: '50%',
                    background: a.swatch,
                    boxShadow: '0 0 0 1px var(--line) inset',
                  }}
                />
                <span style={{ flex: 1, textAlign: 'left' }}>{a.label}</span>
                {accent === a.value && (
                  <span className="material-symbols-outlined" style={{ fontSize: 14, color: 'var(--accent)' }}>check</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Theme toggle removed — the site is light-only. The
          `themeBootstrap` script in app/layout.tsx forces `<html class="light">`
          on every load, overriding any leftover `theme=dark` localStorage
          value and the OS-level prefers-color-scheme. */}

      <div className="divider-vertical" />

      {!user ? (
        <>
          <Link href="/auth/login" className="ps-btn ps-btn-ghost ps-btn-sm no-underline topbar-auth-link topbar-login-link">{t('nav.sign_in')}</Link>
          <Link href="/auth/login" className="ps-btn ps-btn-primary ps-btn-sm no-underline topbar-auth-link">{t('topbar.sign_up')}</Link>
        </>
      ) : (
        <>
          <Link
            href="/my/saved"
            className="ps-btn ps-btn-icon ps-btn-ghost no-underline topbar-user-shortcut"
            aria-label={t('topbar.my_collections')}
            title={t('topbar.my_collections')}
          >
            <span className="material-symbols-outlined ps-icon">bookmarks</span>
          </Link>
          <Link
            href="/my/prompts"
            className="ps-btn ps-btn-icon ps-btn-ghost no-underline topbar-user-shortcut"
            aria-label={t('topbar.my_prompts')}
            title={t('topbar.my_prompts')}
          >
            <span className="material-symbols-outlined ps-icon">auto_awesome</span>
          </Link>
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setDropdownOpen((o) => !o)}
            className="ps-btn ps-btn-icon ps-btn-ghost"
            aria-label="Account menu"
          >
            {user.avatar_url ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={user.avatar_url} alt={user.name}
                style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover' }} />
            ) : (
              <span className="material-symbols-outlined ps-icon">account_circle</span>
            )}
          </button>

          {dropdownOpen && (
            <div
              className="absolute right-0 mt-2 w-56 py-1 z-50"
              style={{
                background: 'var(--bg-raised)',
                border: '1px solid var(--line)',
                borderRadius: 'var(--radius-md)',
                boxShadow: 'var(--shadow-lg)',
              }}
            >
              <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--line)' }}>
                <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>{user.name}</p>
                <p className="text-xs" style={{ color: 'var(--ink-3)' }}>@{user.username}</p>
              </div>
              <Link href={`/profile/${user.username}`} onClick={() => setDropdownOpen(false)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm no-underline hover:bg-surface-container-low"
                style={{ color: 'var(--ink)' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>person</span>
                {t('topbar.view_profile')}
              </Link>
              <Link href="/my/prompts" onClick={() => setDropdownOpen(false)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm no-underline hover:bg-surface-container-low"
                style={{ color: 'var(--ink)' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>grid_view</span>
                {t('nav.my_prompts')}
              </Link>
              <Link href="/my/saved" onClick={() => setDropdownOpen(false)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm no-underline hover:bg-surface-container-low"
                style={{ color: 'var(--ink)' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>bookmark</span>
                {t('nav.saved')}
              </Link>
              <Link href="/my/reports" onClick={() => setDropdownOpen(false)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm no-underline hover:bg-surface-container-low"
                style={{ color: 'var(--ink)' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>flag</span>
                {t('nav.my_reports')}
              </Link>
              <button onClick={handleLogout}
                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm"
                style={{ color: 'var(--color-error)' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>logout</span>
                {t('topbar.logout')}
              </button>
            </div>
          )}
        </div>
        </>
      )}
    </div>
  );
}
